import { useState, useEffect } from 'react';
import { handlePresaleTransaction, handleMoonPayTransaction, SUPPORTED_TOKENS, connectWallet } from '../services/presaleService';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Presale = () => {
  const [selectedCurrency, setSelectedCurrency] = useState('USDT');
  const [amount, setAmount] = useState('');
  const [metamaskLoading, setMetamaskLoading] = useState(false);
  const [moonpayLoading, setMoonpayLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [transactionHash, setTransactionHash] = useState('');
  const [successType, setSuccessType] = useState('');
  const [whitelistAddress, setWhitelistAddress] = useState('');
  const [whitelistStatus, setWhitelistStatus] = useState('');
  const [referralStats, setReferralStats] = useState(null);
  const [investments, setInvestments] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const currencies = [ 
    { symbol: 'USDT', name: SUPPORTED_TOKENS.USDT.name },
    { symbol: 'USDC', name: SUPPORTED_TOKENS.USDC.name }
  ];

  const handleMetaMaskPresale = async (e) => {
    e.preventDefault();
    setError('');
    setMetamaskLoading(true);
    
    try {
      // First connect wallet
      await connectWallet();
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setWalletAddress(accounts[0]);
      await fetchInvestments(accounts[0]);
      
      // Get referral code from URL if present
      const urlParams = new URLSearchParams(location.search);
      const referralCode = urlParams.get('ref');

      // Then handle transaction
      const result = await handlePresaleTransaction(amount, selectedCurrency, referralCode);
      if (result.success) {
        setTransactionHash(result.hash);
        setSuccessType('purchase');
        setShowSuccess(true);
        setShowDashboard(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setMetamaskLoading(false);
    }
  };

  const handleMoonPay = async (e) => {
    e.preventDefault();
    setError('');
    setMoonpayLoading(true);
    try {
      const result = await handleMoonPayTransaction(amount, selectedCurrency);
      if (result.moonPayUrl) {
        window.open(result.moonPayUrl, '_blank');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setMoonpayLoading(false);
    }
  };

  const handleWhitelist = async (e) => {
    e.preventDefault();
    setError(null);
    setWhitelistStatus('processing');
    try {
      const response = await fetch('/api/presale/whitelist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address: whitelistAddress }),
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        setWhitelistAddress('');
        setError(null);
        setWhitelistStatus('success');
        setSuccessType('whitelist');
        setShowSuccess(true);
      } else if (data.message) {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error('Whitelist error:', err);
      setError(err.message);
      setShowSuccess(false);
      setWhitelistStatus('error');
    }
  };

  const handleTwitterShare = () => {
    const tweetText = encodeURIComponent(
      `🚀 Join me in the @BitSport presale! Get BITP tokens at the best price and earn rewards!\n\nUse my referral link to get started:`
    );
    const referralUrl = encodeURIComponent(
      referralStats?.referralCode 
        ? `${window.location.origin}${window.location.pathname}?ref=${referralStats.referralCode}`
        : `${window.location.origin}${window.location.pathname}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}&url=${referralUrl}`, '_blank');
  };

  const fetchInvestments = async (address) => {
    try {
      const response = await fetch(`/api/presale/participant/${address}`);
      const data = await response.json();
      if (data.success) {
        setInvestments(data.data);
        if (data.data && data.data.length > 0) {
          setShowDashboard(true);
        }
      }
    } catch (err) {
      console.error('Failed to load investment data:', err);
    }
  };

  useEffect(() => {
    const fetchReferralStats = async () => {
      if (walletAddress) {
        try {
          const response = await fetch(`/api/presale/referral-stats/${walletAddress}`);
          const data = await response.json();
          if (data.success) {
            setReferralStats(data.data);
          }
        } catch (err) {
          console.error('Error fetching referral stats:', err);
        }
      }
    };
    if (walletAddress) fetchInvestments(walletAddress);
    fetchReferralStats();
  }, [walletAddress]);

  const SuccessModal = () => (
    <AnimatePresence>
      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/80"
        >
          <motion.div
            initial={{ y: 50 }}
            animate={{ y: 0 }}
            className="bg-dark-lighter rounded-lg p-8 max-w-md w-full mx-4 relative overflow-hidden"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center"
            >
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
            <h3 className="text-2xl font-bold text-white text-center mb-4">
              {successType === 'purchase' ? 'Transaction Successful!' : 'Whitelist Registration Successful!'}
            </h3>
            <div className="text-gray-300 text-center mb-6">
              {successType === 'purchase' ? (
                <>
                  <p className="mb-2">Your investment has been confirmed.</p>
                  <p className="text-sm">Transaction Hash:</p>
                  <a 
                    href={`https://bscscan.com/tx/${transactionHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 text-sm break-all"
                  >
                    {transactionHash}
                  </a>
                </>
              ) : (
                <p className="mb-2">Your wallet has been successfully whitelisted for the presale.</p>
              )}
            </div>
            <div className="flex justify-center space-x-4">
              <button onClick={handleTwitterShare} className="bg-[#1DA1F2] text-white px-6 py-2 rounded-lg hover:bg-[#1a8cd8]">Share</button>
              {successType === 'purchase' && (
                <button onClick={() => setShowSuccess(false)} className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90">View Dashboard</button>
              )}
              {successType === 'whitelist' && (
                <button onClick={() => { setShowSuccess(false); document.getElementById('presale-section').scrollIntoView({ behavior: 'smooth' }); }} className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90">Continue to Presale</button>
              )}
              <button onClick={() => { setShowSuccess(false); navigate('/'); }} className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700">Home</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-4">BITP Dashboard</h1>
        
        <div className="bg-dark-lighter rounded-lg p-6 mb-8">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-gray-300">
              <p>Status</p>
              <p className="text-xl font-semibold text-primary">
                Sale Started
              </p>
            </div>
            <div className="text-gray-300">
              <p>Price</p>
              <p className="text-xl font-semibold text-primary">$0.03</p>
            </div>
            <div className="text-gray-300">
              <p>Total Supply</p>
              <p className="text-xl font-semibold text-primary">500M BITP</p>
            </div>
          </div>
          
          <div className="flex gap-4 mb-6">
            {!walletAddress && (
              <button
                onClick={() => {
                  document.getElementById('presale-section').scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex-1 bg-primary text-white py-3 px-6 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Join PreSale
              </button>
            )}
            <button
              onClick={handleTwitterShare}
              className="flex-1 bg-[#1DA1F2] text-white py-3 px-6 rounded-lg hover:bg-[#1a8cd8] transition-colors"
            >
              Share on Twitter
            </button>
            {walletAddress && investments && investments.length > 0 && (
              <button className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors">
                Claim Available Tokens
              </button>
            )}
          </div>
        </div>

        <div className="bg-dark-lighter rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Your BITP Status</h2>
            <button
              onClick={() => setShowDashboard(!showDashboard)}
              className="text-primary hover:text-primary/80"
            >
              {showDashboard ? 'Hide Details' : 'Show Details'}
            </button>
          </div>
          
          <div className="space-y-6">
            {(!investments || investments.length === 0) && (
              <div className="bg-dark rounded-lg p-6">
                <div className="text-center">
                  <p className="text-gray-400 mb-4">Welcome to BITP! Get started by:</p>
                  <ul className="text-gray-300 mt-2 space-y-2 text-left list-disc pl-6 mb-6">
                    <li>Connecting your wallet</li>
                    <li>Participating in the presale</li>
                    <li>Sharing with friends to earn 5% rewards</li>
                  </ul>
                  <button
                    onClick={handleTwitterShare}
                    className="w-full bg-[#1DA1F2] text-white py-3 px-6 rounded-lg hover:bg-[#1a8cd8] transition-colors mb-4"
                  >
                    Share on Twitter
                  </button>
                </div>
              </div>
            )}
            {investments && investments.map((investment, index) => (
              <div key={index} className="bg-dark rounded-lg p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <p className="text-gray-400">Investment Amount</p>
                    <p className="text-xl font-semibold text-white">
                      {investment.contribution.amount} {investment.contribution.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">BITP Allocation</p>
                    <p className="text-xl font-semibold text-white">
                      {investment.bitpAllocation.toFixed(2)} BITP
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">TGE Amount (20%)</p>
                    <p className="text-xl font-semibold text-white">
                      {investment.vestingSchedule.tgeAmount.toFixed(2)} BITP
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Monthly Unlock (10%)</p>
                    <p className="text-xl font-semibold text-white">
                      {investment.vestingSchedule.monthlyAmount.toFixed(2)} BITP
                    </p>
                  </div>
                </div>

                {showDashboard && (
                  <div className="space-y-4">
                    <div className="bg-dark-lighter rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-4">Vesting Schedule</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-gray-300">
                          <span>Next Vesting Date:</span>
                          <span>{new Date(investment.vestingSchedule.nextVestingDate).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span>Remaining Months:</span>
                          <span>{investment.vestingSchedule.remainingMonths}</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span>Transaction Hash:</span>
                          <a href={`https://bscscan.com/tx/${investment.transactionHash}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">
                            {investment.transactionHash.slice(0, 8)}...{investment.transactionHash.slice(-6)}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-dark-lighter rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Whitelist Registration</h2>
          <div className="space-y-4">
            <p className="text-gray-300">Register your wallet address for the presale whitelist to ensure your participation.</p>
            
            <div className="flex gap-4">
              <input
                type="text"
                value={whitelistAddress}
                onChange={(e) => setWhitelistAddress(e.target.value)}
                placeholder="Enter wallet address (0x...)"
                className="flex-1 bg-dark border border-gray-600 text-white rounded-lg p-3 focus:outline-none focus:border-primary"
              />
              <button
                onClick={handleWhitelist}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
                disabled={whitelistStatus === 'processing' || !whitelistAddress}
              >
                {whitelistStatus === 'processing' ? 'Processing...' : 'Register'}
              </button>
            </div>

          </div>
        </div>

        <div id="presale-section" className="bg-dark-lighter rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Purchase BITP Tokens</h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-gray-300">
              <p>Current Price</p>
              <p className="text-xl font-semibold text-primary">$0.03</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-gray-300 mb-2">Select Currency</label>
              <select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="w-full bg-dark border border-gray-600 text-white rounded-lg p-3 focus:outline-none focus:border-primary"
              >
                {currencies.map((currency) => (
                  <option key={currency.symbol} value={currency.symbol}>
                    {currency.symbol} - {currency.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Amount</label>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full bg-dark border border-gray-600 text-white rounded-lg p-3 focus:outline-none focus:border-primary"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  {selectedCurrency}
                </div>
              </div>
            </div>

            <div className="bg-dark rounded-lg p-4">
              <div className="flex justify-between text-gray-300 mb-2">
                <span>You will receive:</span>
                <span>{amount ? (Number(amount) / 0.03).toFixed(2) : '0'} BITP</span>
              </div>
              <div className="flex justify-between text-gray-300 text-sm">
                <span>Exchange rate:</span>
                <span>1 {selectedCurrency} = {(1 / 0.03).toFixed(2)} BITP</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleMetaMaskPresale}
              id="metamask"
              disabled={metamaskLoading || moonpayLoading}
              className="w-full bg-primary text-white py-3 px-6 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 mb-3"
            >
              {metamaskLoading ? 'Processing...' : 'Purchase with MetaMask'}
            </button>
            
            <button
              type="button"
              onClick={handleMoonPay}
              id="moonpay"
              disabled={metamaskLoading || moonpayLoading}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {moonpayLoading ? 'Processing...' : 'Purchase with Credit Card'}
            </button>


            {referralStats && (
              <div className="mt-4 p-4 bg-dark rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-2">Your Referral Stats</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-300">
                    <span>Total Referrals:</span>
                    <span>{referralStats.totalReferrals}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Total Rewards:</span>
                    <span>{referralStats.totalReferralBonus.toFixed(2)} BITP</span>
                  </div>
                  <div className="text-sm text-gray-400 mt-2">
                    Share your unique link to earn 5% of referred purchases in BITP tokens!
                  </div>
                </div>
              </div>
            )}

            {error && (
              <p className="text-red-500 mt-2 text-sm">{error}</p>
            )}
          </div>
        </div>

        <div className="bg-dark-lighter rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Presale Information</h2>
          <div className="space-y-4 text-gray-300">
            <p>• Minimum Purchase: 2 USDT/USDC</p>
            <p>• Maximum Purchase: 10,000 USDT/USDC</p>
            <p>• Presale Duration: 30 days</p>
            <p>• Credit Card purchases available via MoonPay</p>
            <div>
              <p>• Token Distribution Schedule:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>20% at Token Generation Event (TGE)</li>
                <li>Remaining 80% vested over 8 months</li>
                <li>10% unlocked monthly after TGE</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <SuccessModal />
    </div>
  );
};

export default Presale;
